import _ from 'lodash'
import Utils from '@/utils/utils'

import { OrderEditorCutoutTypes, OrderEditorGeometryShapeTypes } from '@/store/types'
import GlassDesignerTypes from '../types'

/**
 * Shape Cutout class
 * Used internally when calculating cutouts in a glass shape and converting to CNC paths and OrderItems
 */
export default class ShapeCutout {
  constructor(cutoutData = {}) {
    this.type = cutoutData.type || ''
    this.geometryShapeType = cutoutData.geometryShapeType || ''
    this.points = cutoutData.points || []
    this.displayPointList = cutoutData.displayPointList || []
    this.pathSegmentList = cutoutData.pathSegmentList || []
    this.segmentIdList = cutoutData.segmentIdList || []
    this.pathLength = cutoutData.pathLength || 0
    this.bounds = cutoutData.bounds || null
    this.radius = cutoutData.radius || 0
    this.maxDimension = cutoutData.maxDimension || null
    this.cutoutWidth = cutoutData.cutoutWidth || null
    this.cutoutHeight = cutoutData.cutoutHeight || null
    this.isCutoutPolished = cutoutData.isCutoutPolished || false
    this.isCountersunk = cutoutData.isCountersunk || false
    this.countersunkRadius = cutoutData.countersunkRadius || 0
    this.cutoutObjectId = cutoutData.cutoutObjectId || null
  }


  /**
   * Generate a description for the OrderItem based on the shape cutout details
   * 
   * @param {Object} cutoutStockInfo - The object for the OrderEditorCutoutStock record from the server that matches the cutout
   * @param {Object} stockItem - The Stock
   * @return {String} Returns the description of the shape cutout
   */
  generateDescription(cutoutStockInfo, stockItem) {
    var description = ''
    if(cutoutStockInfo)
      description = cutoutStockInfo.description || ''
    if(description.trim() == '')
      description = stockItem.name

    let maxDimension = Math.round(parseFloat(this.maxDimension || 0))
    var cutoutWidth = Math.round(parseFloat(this.cutoutWidth || 0))
    var cutoutHeight = Math.round(parseFloat(this.cutoutHeight || 0))

    // Add in size details for center cutouts
    if(this.type == OrderEditorCutoutTypes.centerCutOut) {
      if(this.geometryShapeType == OrderEditorGeometryShapeTypes.circle) {
        // Add diameter
        if(maxDimension > 0)
          description += ` ${maxDimension.toFixed(0)}mm`
      }

      if(this.geometryShapeType == OrderEditorGeometryShapeTypes.rectangle) {
        // Add size
        if(cutoutWidth > 0 && cutoutHeight > 0)
          description += ` ${cutoutHeight.toFixed(0)}x${cutoutWidth.toFixed(0)}`
      }
    }

    return description
  }


  /**
   * Generate a detailed review details for a shape cutout
   * Includes measure to, position from nearby objects, etc
   * 
   */
  generateReviewDetails(glassShape, shapeGroup) {
    var detailList = []

    let maxDimension = Math.round(parseFloat(this.maxDimension || 0))
    var cutoutWidth = Math.round(parseFloat(this.cutoutWidth || 0))
    var cutoutHeight = Math.round(parseFloat(this.cutoutHeight || 0))
    
    if(this.type == OrderEditorCutoutTypes.centerCutOut && this.cutoutObjectId) {
      // Get glass hole to get info
      var centerCutoutObj = glassShape.getCenterCutoutList().find(centerCutout => centerCutout.id == this.cutoutObjectId)
      if(centerCutoutObj) {

        // Generate cutout total size info
        if(this.geometryShapeType == OrderEditorGeometryShapeTypes.circle) {
          // Add diameter
          if(maxDimension > 0)
            detailList.push(`Diameter: ${maxDimension.toFixed(0)}mm`)
        }
  
        if(this.geometryShapeType == OrderEditorGeometryShapeTypes.rectangle) {
          // Add size
          if(cutoutWidth > 0 && cutoutHeight > 0)
            detailList.push(`Size: ${cutoutHeight.toFixed(0)}x${cutoutWidth.toFixed(0)}`)
        }

        // Datum links
        detailList = _.concat(detailList, centerCutoutObj.datumLinks.map(datumLink => {
          var sourceDatumLookup = {
            glassSegment: 'Panel Edge',
            glassSegmentStart: 'Panel Edge',
            glassSegmentEnd: 'Panel Edge',
            shapeCircleEdge: 'Panel Edge',
            shapeBoundsEdge: 'Panel Edge',
            datumLine: 'Datum',
            hole: 'Center Cutout',
            position: 'Position'
          }
          var srcPosition = sourceDatumLookup[datumLink.sourceDatumType]

          var destPosition = 'Center'
          if(centerCutoutObj.measureToVertical != GlassDesignerTypes.HoleMeasurePointTypes.middle || centerCutoutObj.measureToHorizontal != GlassDesignerTypes.HoleMeasurePointTypes.center) {
            destPosition = Utils.getLookupName(GlassDesignerTypes.HoleMeasurePointTypeLookup, centerCutoutObj.measureToVertical)
            destPosition += ', ' + Utils.getLookupName(GlassDesignerTypes.HoleMeasurePointTypeLookup, centerCutoutObj.measureToHorizontal)
          }

          return `${datumLink.sourceOffsetDistance.toFixed(0)}mm from ${srcPosition} to ${destPosition}`
        }))

        return detailList
      }
    }

    // Note: Only handle grouped edge/corner cutouts for now
    if(this.type == OrderEditorCutoutTypes.cornerCutOut || this.type == OrderEditorCutoutTypes.edgeCutOut) {

      // Generate cutout total size info
      detailList.push(`Size: ${cutoutHeight.toFixed(0)}mm x ${cutoutWidth.toFixed(0)}mm`)

      if(shapeGroup) {
        let segmentLinkInfo = glassShape.getSegmentStartEndLinksForShapeGroup(shapeGroup)

        let startSegment = glassShape.getSegmentById(segmentLinkInfo.prevSegmentId)
        let endSegment = glassShape.getSegmentById(segmentLinkInfo.nextSegmentId)

        if(startSegment && startSegment.type == GlassDesignerTypes.SegmentTypes.line)
          detailList.push(this._generateReviewCutoutDistanceForSegment(glassShape, shapeGroup, startSegment))

        if(endSegment && endSegment.type == GlassDesignerTypes.SegmentTypes.line)
          detailList.push(this._generateReviewCutoutDistanceForSegment(glassShape, shapeGroup, endSegment))

        return _.compact(detailList)
      }

      // Basic cutouts without groups
      // Calculate the segments used in the cutout
      var groupSegmentList = this._calculateCutoutSegments(glassShape)

      let segmentLinkInfo = glassShape.getSegmentStartEndLinksForSegmentIdList(groupSegmentList.map(segment => segment.id))
      let startSegment = glassShape.getSegmentById(segmentLinkInfo.prevSegmentId)
      let endSegment = glassShape.getSegmentById(segmentLinkInfo.nextSegmentId)

      if(startSegment && startSegment.type == GlassDesignerTypes.SegmentTypes.line)
        detailList.push(this._generateReviewCutoutDistanceForSegment(glassShape, null, startSegment))

      if(endSegment && endSegment.type == GlassDesignerTypes.SegmentTypes.line)
        detailList.push(this._generateReviewCutoutDistanceForSegment(glassShape, null, endSegment))

      return _.compact(detailList)
    }

    return []
  }


  /**
   * Generate the cutout distance detail for the review system
   * Based on segment in the shape. Assumes the segment is a straight line
   * 
   */
  _generateReviewCutoutDistanceForSegment(glassShape, shapeGroup, segment) {
    var srcPositionName = Utils.getLookupName(GlassDesignerTypes.CutoutMeasurementTypeLookup, GlassDesignerTypes.CutoutMeasurementTypes.edge)
    var dstPositionName = Utils.getLookupName(GlassDesignerTypes.CutoutMeasurementTypeLookup, GlassDesignerTypes.CutoutMeasurementTypes.edge)

    var startPosition = segment.startPosition.clone()
    var endPosition = segment.endPosition.clone()
    var segDir = segment.getDirection()

    let segEndGroup = glassShape.getSegmentEndLinkedGroup(segment.id)
    let segStartGroup = glassShape.getSegmentStartLinkedGroup(segment.id)

    if(segStartGroup) {
      if(segStartGroup.cutoutMeasurementType == GlassDesignerTypes.CutoutMeasurementTypes.center) {
        let startGroupDir = segDir.clone().negate()
        let groupRangeDist = segStartGroup.calculateGroupRangeInfoForDirection(startGroupDir, segment.startPosition)
        let startOffset = groupRangeDist.min + (groupRangeDist.max - groupRangeDist.min) / 2.0

        startPosition.addScaledVector(startGroupDir, startOffset)

        srcPositionName = Utils.getLookupName(GlassDesignerTypes.CutoutMeasurementTypeLookup, GlassDesignerTypes.CutoutMeasurementTypes.center)
      }
    }
    if(segEndGroup) {
      if(segEndGroup.cutoutMeasurementType == GlassDesignerTypes.CutoutMeasurementTypes.center) {
        let groupRangeDist = segEndGroup.calculateGroupRangeInfoForDirection(segDir, segment.endPosition)
        let endOffset = groupRangeDist.min + (groupRangeDist.max - groupRangeDist.min) / 2.0

        endPosition.addScaledVector(segDir, endOffset)

        dstPositionName = Utils.getLookupName(GlassDesignerTypes.CutoutMeasurementTypeLookup, GlassDesignerTypes.CutoutMeasurementTypes.center)
      }
    }

    var distance = startPosition.distanceTo(endPosition)
    return `${distance.toFixed(0)}mm from ${srcPositionName} to ${dstPositionName}`
  }


  /**
   * Calculate the segments used for this cutout
   * Note: Only includes segments that midpoint is within the cutout, not just start position
   * 
   * @param {GlassShape} glassShape - GlassShape object to get segments for
   * @return {Array<GlassSegment>} Returns list of GlassSegments from shape that make up this cutout
   */
  _calculateCutoutSegments(glassShape) {
    let vertexThreshold = 0.1
    let groupSegmentList = []

    if(this.pathSegmentList.length < 1)
      return []

    let startSegmentIndex = glassShape.calculateClosestSegmentStartToPoint(this.pathSegmentList[0].startPosition, [], vertexThreshold)
    let endSegmentIndex = glassShape.calculateClosestSegmentStartToPoint(this.pathSegmentList[this.pathSegmentList.length-1].endPosition, [], vertexThreshold)
    if(startSegmentIndex == -1 || endSegmentIndex == -1)
      return []

    var cutoutStarted = false
    for(let i=0; i<glassShape.segmentList.length*2; i++) {
      let index = i % glassShape.segmentList.length

      if(index == startSegmentIndex)
        cutoutStarted = true
      if(cutoutStarted) {
        if(index == endSegmentIndex)
          break

        groupSegmentList.push(glassShape.segmentList[index])
      }
    }

    return groupSegmentList
  }

}